import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserRole, type Empresa, type User } from '../types';
import { queryClient } from '../queryClient';

/** Primer elemento de las queryKeys que son del usuario, no de la organización. */
const DE_USUARIO: ReadonlySet<unknown> = new Set(['auth', 'mi-perfil']);

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  empresas: Empresa[];
  empresaActivaId: string | null;
  /**
   * true si el usuario eligió "Todas (vista global)" a mano. Sin esto, un
   * Medicus con una sola organización volvía a quedar fijado a ella en cada
   * respuesta de `/me` (recarga, refresh del token) aunque hubiera elegido
   * "Todas": el selector se veía saltar solo. Se persiste junto con la activa.
   */
  vistaGlobal: boolean;
  setSession: (token: string, user: User, refreshToken?: string) => void;
  /** Actualiza solo los tokens (usado por el refresh automático en api.ts). */
  setTokens: (token: string, refreshToken: string | null) => void;
  setEmpresas: (empresas: Empresa[]) => void;
  setEmpresaActiva: (id: string | null) => void;
  logout: () => void;
}

/**
 * Organización activa que corresponde, dado el usuario, las organizaciones que
 * puede ver (las que devolvió `/auth/me`) y la que estaba activa.
 *
 *  - Usuario de organización: solo las suyas (`user.empresaIds`). Si la activa
 *    ya no es suya (el SA se la quitó) se descarta; si no hay activa, la
 *    primera: la primera request tras el login ya tiene que mandar
 *    `X-Empresa-Id` o da 403.
 *  - Roles Medicus: `user.empresaIds` viene VACÍO (ven todas), así que se
 *    valida contra `empresas`. Se conserva la activa si sigue accesible. Sin
 *    activa, se fija la organización solo cuando hay UNA: con una sola el
 *    selector se oculta y no habría forma de elegirla, y las acciones que
 *    exigen organización (cargar modificaciones) rebotarían con "hay que
 *    indicar la organización". Con varias queda la vista global hasta que el
 *    usuario elija. Mientras `empresas` no haya llegado (login, antes de
 *    `/me`) se deja lo que había; si llegó y está VACÍA (no queda ninguna
 *    organización), la activa se descarta: un id persistido de una
 *    organización borrada viajaría como `X-Empresa-Id` en cada request y
 *    dejaría todas las listas vacías sin selector para sacarlo.
 *
 * Antes `setSession` validaba SIEMPRE contra `user.empresaIds`, y para un
 * Medicus eso pisaba con `null` la organización que `setEmpresas` acababa de
 * fijar: ningún Medicus mandaba nunca `X-Empresa-Id`.
 */
export function reconciliarEmpresaActiva(
  user: Pick<User, 'rol' | 'empresaIds'> | null,
  empresas: Empresa[],
  activa: string | null,
  /** false = `/me` todavía no respondió: `empresas` vacía no significa "no hay". */
  empresasCargadas = true,
  /** true = el usuario eligió "Todas" a mano: se respeta, no se vuelve a fijar la única. */
  vistaGlobalElegida = false,
): string | null {
  if (!user) return activa;
  const deOrganizacion = user.rol === UserRole.USUARIO_EMPRESA;
  if (!deOrganizacion && !empresasCargadas) return activa;
  const accesibles = deOrganizacion ? user.empresaIds : empresas.map((e) => e.id);
  if (activa && accesibles.includes(activa)) return activa;
  if (deOrganizacion) return accesibles[0] ?? null;
  if (vistaGlobalElegida) return null;
  return accesibles.length === 1 ? accesibles[0] : null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      empresas: [],
      empresaActivaId: null,
      vistaGlobal: false,
      setSession: (token, user, refreshToken) => {
        const { empresas, empresaActivaId, vistaGlobal } = get();
        // En el login `empresas` todavía está vacía porque `/me` no corrió:
        // no es "no hay organizaciones", es "no se cargaron". Si `/me` ya
        // corrió y devolvió vacío, `setEmpresas([])` ya dejó la activa en null.
        const cargadas = empresas.length > 0;
        set({
          token,
          user,
          empresaActivaId: reconciliarEmpresaActiva(
            user,
            empresas,
            empresaActivaId,
            cargadas,
            vistaGlobal,
          ),
          // Solo pisa refreshToken si vino (login). En la re-hidratación de
          // `me()` no se pasa → se conserva el persistido.
          ...(refreshToken !== undefined ? { refreshToken } : {}),
        });
      },
      setTokens: (token, refreshToken) => set({ token, refreshToken }),
      setEmpresas: (empresas) => {
        const { user, empresaActivaId, vistaGlobal } = get();
        set({
          empresas,
          empresaActivaId: reconciliarEmpresaActiva(
            user,
            empresas,
            empresaActivaId,
            true,
            vistaGlobal,
          ),
        });
      },
      // Elegir a mano: `null` es "Todas" y se recuerda como elección explícita.
      setEmpresaActiva: (id) => {
        const cambia = id !== get().empresaActivaId;
        set({ empresaActivaId: id, vistaGlobal: id === null });
        // Toda la data cacheada es de la organización ANTERIOR: el server
        // scopea por el header `X-Empresa-Id` que sale de este store, pero
        // ninguna queryKey lleva la organización. Sin esto, con `staleTime` de
        // 30 s el usuario seguía viendo los padrones y legajos de la otra.
        //
        // `resetQueries` y no `removeQueries`: `remove` saca la query de la
        // caché pero NO avisa a los componentes montados, que siguen mostrando
        // el dato viejo hasta que algo los re-renderice (en Búsqueda o el
        // Dashboard, nunca). `reset` vuelve cada query a su estado inicial y
        // refetchea las que están en pantalla.
        //
        // Lo que es por USUARIO y no por organización se conserva: la sesión
        // (`auth`) y el perfil (`mi-perfil`, que alimenta el selector de
        // organización de la carga de padrones — resetearlo lo dejaba un
        // instante en "Cargando organizaciones…" y deshacía la elección).
        if (cambia) {
          // Sin `await` ni `void` (Sonar S3735): la promesa resuelve cuando
          // terminan los refetches, y acá nadie tiene que esperarlos.
          queryClient.resetQueries({ predicate: (q) => !DE_USUARIO.has(q.queryKey[0]) });
        }
      },
      logout: () =>
        set({
          token: null,
          refreshToken: null,
          user: null,
          empresas: [],
          empresaActivaId: null,
          vistaGlobal: false,
        }),
    }),
    {
      name: 'cargamasiva.auth',
      partialize: (s) => ({
        token: s.token,
        refreshToken: s.refreshToken,
        user: s.user,
        empresaActivaId: s.empresaActivaId,
        vistaGlobal: s.vistaGlobal,
      }),
    },
  ),
);
